import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import type {
  AdminActionActor,
  AdminActionHistoryItem,
} from '../../../../services/adminAgent.service';
import { formatDateTime } from '../../../../utils/dateFormat';
import { ActionJsonBlock } from './ActionJsonBlock';
import { ACTION_STATUS_STYLES } from './actionStatusStyles';

const actorLabel = (actor: AdminActionActor | null) =>
  actor ? (actor.fullName ? `${actor.fullName} (${actor.email})` : actor.email) : '—';

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <div className="text-[11px] font-medium uppercase text-slate-500 dark:text-slate-400">
      {label}
    </div>
    <div className="break-words text-sm text-slate-900 dark:text-slate-100">{children}</div>
  </div>
);

interface ActionHistoryDetailDialogProps {
  action: AdminActionHistoryItem | null;
  onClose: () => void;
}

export const ActionHistoryDetailDialog: React.FC<ActionHistoryDetailDialogProps> = ({
  action,
  onClose,
}) => (
  <Dialog open={action !== null} onClose={onClose} maxWidth="md" fullWidth>
    <DialogTitle className="dark:text-slate-100">Assistant action</DialogTitle>
    <DialogContent>
      {action && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-slate-900 dark:text-slate-100">
              {action.operationId}
            </span>
            <span
              className={`rounded-full px-2 py-0.5 text-xs ${ACTION_STATUS_STYLES[action.status]}`}
            >
              {action.status}
            </span>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700 dark:bg-slate-700 dark:text-slate-200">
              {action.riskLevel}
            </span>
          </div>
          <div className="break-all font-mono text-xs text-slate-500 dark:text-slate-400">
            {action.method} {action.path}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Requested by">{actorLabel(action.requestedBy)}</Field>
            <Field label="Decided by">{actorLabel(action.approver)}</Field>
            <Field label="Created">{formatDateTime(action.createdAt, true) || '—'}</Field>
            <Field label="Decided">{formatDateTime(action.decidedAt, true) || '—'}</Field>
            <Field label="Executed">{formatDateTime(action.executedAt, true) || '—'}</Field>
            <Field label="Response status">{action.responseStatus ?? '—'}</Field>
          </div>

          {action.reason && <Field label="Reason">{action.reason}</Field>}
          {action.resultSummary && <Field label="Result">{action.resultSummary}</Field>}
          <ActionJsonBlock label="Data before" value={action.preview} />
          <ActionJsonBlock label="Query" value={action.query} />
          <ActionJsonBlock label="Body" value={action.body} />
        </div>
      )}
    </DialogContent>
    <DialogActions className="px-6 pb-4">
      <Button onClick={onClose} variant="outlined">
        Close
      </Button>
    </DialogActions>
  </Dialog>
);
